import { __extends } from "tslib";
import { emitEvent, listenOwnProperty } from '@tyler-components-web/core';
import { getActiveElement } from 'shadow-utils';
import { CalendarDropdown } from '../calendar-dropdown/calendar-dropdown';
import { BaseAdapter, DateInputMask, randomChars } from '../core';
import { ICON_BUTTON_CONSTANTS } from '../icon-button';
import { TEXT_FIELD_CONSTANTS } from '../text-field';
import { DATE_RANGE_PICKER_CONSTANTS } from './date-range-picker-constants';
import { ICON_CONSTANTS } from '../icon';
var DateRangePickerAdapter = /** @class */ (function (_super) {
    __extends(DateRangePickerAdapter, _super);
    function DateRangePickerAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._identifier = randomChars();
        return _this;
    }
    DateRangePickerAdapter.prototype.initialize = function () {
        this._fromInputElement = this._component.querySelectorAll(DATE_RANGE_PICKER_CONSTANTS.selectors.INPUT)[0];
        this._toInputElement = this._component.querySelectorAll(DATE_RANGE_PICKER_CONSTANTS.selectors.INPUT)[1];
        if (!this._fromInputElement || !this._fromInputElement) {
            throw new Error("the " + this._component.localName + " requires two inputs");
        }
        this._calendarDropdown = new CalendarDropdown(this._fromInputElement);
        this._calendarDropdown.range = true;
    };
    DateRangePickerAdapter.prototype.destroy = function () {
        this._calendarDropdown.destroy();
    };
    DateRangePickerAdapter.prototype.initializeMask = function (fromOptions, toOptions) {
        if (this._fromInputMask) {
            this._fromInputMask.destroy();
        }
        if (this._toInputMask) {
            this._toInputMask.destroy();
        }
        this._fromInputMask = new DateInputMask(this._fromInputElement, fromOptions);
        this._toInputMask = new DateInputMask(this._toInputElement, toOptions);
    };
    DateRangePickerAdapter.prototype.destroyMask = function () {
        if (this._fromInputMask) {
            this._fromInputMask.destroy();
            this._fromInputMask = undefined;
        }
        if (this._toInputMask) {
            this._toInputMask.destroy();
            this._toInputMask = undefined;
        }
    };
    DateRangePickerAdapter.prototype.initializeAccessibility = function () {
        var _this = this;
        this._applyToInputs(function (input) { return input.setAttribute('autocomplete', 'off'); });
        this._applyToInputs(function (input) { return input.setAttribute('autocorrect', 'off'); });
        this._applyToInputs(function (input) { return input.setAttribute('autocapitalize', 'off'); });
        this._applyToInputs(function (input) { return input.setAttribute('spellcheck', 'false'); });
        this._applyToInputs(function (input) { return input.setAttribute('role', 'combobox'); });
        this._applyToInputs(function (input) { return input.setAttribute('aria-live', 'assertive'); });
        this._applyToInputs(function (input) { return input.setAttribute('aria-atomic', 'true'); });
        this._applyToInputs(function (input) { return input.setAttribute('aria-haspopup', 'true'); });
        this._applyToInputs(function (input) { return input.setAttribute('aria-expanded', 'false'); });
        this._applyToInputs(function (input) { return input.setAttribute('aria-owns', "tcw-date-range-picker-" + _this._identifier); });
    };
    DateRangePickerAdapter.prototype.addFromInputListener = function (type, listener) {
        this._fromInputElement.addEventListener(type, listener);
    };
    DateRangePickerAdapter.prototype.addToInputListener = function (type, listener) {
        this._toInputElement.addEventListener(type, listener);
    };
    DateRangePickerAdapter.prototype.removeFromInputListener = function (type, listener) {
        if (this._fromInputElement) {
            this._fromInputElement.removeEventListener(type, listener);
        }
        if (this._toInputElement) {
            this._toInputElement.removeEventListener(type, listener);
        }
    };
    DateRangePickerAdapter.prototype.addToggleListener = function (type, listener) {
        if (this._toggleElement) {
            this._toggleElement.addEventListener(type, listener);
        }
    };
    DateRangePickerAdapter.prototype.removeToggleListener = function (type, listener) {
        if (this._toggleElement) {
            this._toggleElement.removeEventListener(type, listener);
        }
    };
    DateRangePickerAdapter.prototype.hasInputElement = function () {
        return !!this._fromInputElement && !!this._toInputElement;
    };
    DateRangePickerAdapter.prototype.attachCalendar = function (calendarConfig) {
        Object.assign(this._calendarDropdown, calendarConfig);
        this._calendarDropdown.id = "tcw-date-range-picker-" + this._identifier;
        this._calendarDropdown.range = true;
        this._calendarDropdown.open = true;
        this._fromInputElement.setAttribute('aria-expanded', 'true');
    };
    DateRangePickerAdapter.prototype.detachCalendar = function () {
        if (this._fromInputElement) {
            this._fromInputElement.setAttribute('aria-expanded', 'false');
            this._fromInputElement.removeAttribute('aria-activedescendant');
        }
        if (this._calendarDropdown && this._calendarDropdown.open) {
            this._calendarDropdown.open = false;
        }
    };
    DateRangePickerAdapter.prototype.setActiveDescendant = function (id) {
        this._fromInputElement.setAttribute('aria-activedescendant', id);
    };
    DateRangePickerAdapter.prototype.setCalendarValue = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.value = value;
        }
    };
    DateRangePickerAdapter.prototype.setCalendarMinDate = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.min = value;
        }
    };
    DateRangePickerAdapter.prototype.setCalendarMaxDate = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.max = value;
        }
    };
    DateRangePickerAdapter.prototype.setCalendarBlockedDates = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.blockedDates = value;
        }
    };
    DateRangePickerAdapter.prototype.setCalendarDisabledDaysOfWeek = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.disabledDaysOfWeek = value;
        }
    };
    DateRangePickerAdapter.prototype.propagateCalendarKey = function (key, shiftKey) {
        if (this._calendarDropdown) {
            this._calendarDropdown.propagateKey(key, shiftKey);
        }
    };
    DateRangePickerAdapter.prototype.setFromInputValue = function (value, emitEvents) {
        if (this._fromInputElement.value === value) {
            return;
        }
        this._fromInputElement.value = value;
        if (this._fromInputMask) {
            this._fromInputMask.updateMask();
        }
        if (emitEvents) {
            this._emitFromInputEvent('change');
            this._emitFromInputEvent('input');
        }
    };
    DateRangePickerAdapter.prototype.setToInputValue = function (value, emitEvents) {
        if (this._toInputElement.value === value) {
            return;
        }
        this._toInputElement.value = value;
        if (this._toInputMask) {
            this._toInputMask.updateMask();
        }
        if (emitEvents) {
            this._emitToInputEvent('change');
            this._emitToInputEvent('input');
        }
    };
    DateRangePickerAdapter.prototype.isInputDisabled = function () {
        return this._fromInputElement.disabled;
    };
    DateRangePickerAdapter.prototype.isInputFocused = function () {
        return this._toInputElement === getActiveElement() || this._fromInputElement === getActiveElement();
    };
    DateRangePickerAdapter.prototype.getFromInputValue = function () {
        return this._fromInputElement.value;
    };
    DateRangePickerAdapter.prototype.getToInputValue = function () {
        return this._toInputElement.value;
    };
    DateRangePickerAdapter.prototype.setDisabled = function (isDisabled) {
        this._fromInputElement.disabled = isDisabled;
        this._toInputElement.disabled = isDisabled;
        this._toInputElement.setAttribute('aria-disabled', isDisabled.toString());
        this._fromInputElement.setAttribute('aria-disabled', isDisabled.toString());
        if (this._toggleElement) {
            this._toggleElement.setAttribute('aria-disabled', isDisabled.toString());
            if (this._toggleElement.hasOwnProperty('disabled')) {
                this._toggleElement['disabled'] = isDisabled;
            }
        }
    };
    DateRangePickerAdapter.prototype.setDisableDayCallback = function (disableDayCallback) {
        if (this._calendarDropdown) {
            this._calendarDropdown.disableDayCallback = disableDayCallback;
        }
    };
    DateRangePickerAdapter.prototype.emitFromInputEvent = function (type, data) {
        emitEvent(this._fromInputElement, type, data);
    };
    DateRangePickerAdapter.prototype.emitToInputEvent = function (type, data) {
        emitEvent(this._toInputElement, type, data);
    };
    DateRangePickerAdapter.prototype.tryCreateToggle = function () {
        var tcwTextField = this._component.querySelector(TEXT_FIELD_CONSTANTS.elementName);
        var toggleElement = this._component.querySelector(DATE_RANGE_PICKER_CONSTANTS.selectors.TOGGLE);
        if (tcwTextField) {
            var existingIconButton = tcwTextField.querySelector(ICON_BUTTON_CONSTANTS.elementName + "[slot=trailing]");
            if (existingIconButton || toggleElement) {
                return;
            }
            var iconButtonElement = document.createElement(ICON_BUTTON_CONSTANTS.elementName);
            iconButtonElement.slot = 'trailing';
            iconButtonElement.dense = true;
            iconButtonElement.densityLevel = 3;
            // Override default trailing slot margin in text-field
            iconButtonElement.style.marginRight = '4px';
            var buttonElement = document.createElement('button');
            buttonElement.type = 'button';
            buttonElement.tabIndex = -1;
            buttonElement.setAttribute('aria-label', 'Toggle calendar');
            var iconElement = document.createElement(ICON_CONSTANTS.elementName);
            iconElement.name = 'date_range';
            buttonElement.appendChild(iconElement);
            iconButtonElement.appendChild(buttonElement);
            tcwTextField.appendChild(iconButtonElement);
            this._toggleElement = iconButtonElement;
        }
        else if (toggleElement) {
            this._toggleElement = toggleElement;
        }
    };
    DateRangePickerAdapter.prototype.tryFocusInput = function () {
        this._fromInputElement.focus();
    };
    DateRangePickerAdapter.prototype.tryBlurInput = function () {
        this._fromInputElement.blur();
    };
    DateRangePickerAdapter.prototype.selectFromInputText = function () {
        this._fromInputElement.select();
    };
    DateRangePickerAdapter.prototype.selectToInputText = function () {
        this._toInputElement.select();
    };
    DateRangePickerAdapter.prototype._emitFromInputEvent = function (type) {
        this._fromInputElement.dispatchEvent(new Event(type));
    };
    DateRangePickerAdapter.prototype._emitToInputEvent = function (type) {
        this._toInputElement.dispatchEvent(new Event(type));
    };
    DateRangePickerAdapter.prototype._applyToInputs = function (action) {
        [this._fromInputElement, this._toInputElement].forEach(action);
    };
    DateRangePickerAdapter.prototype.removeToInputListener = function (type, listener) {
        this._toInputElement.removeEventListener(type, listener);
    };
    DateRangePickerAdapter.prototype.setToInputValueChangedListener = function (context, listener) {
        if (this._toInputElement.hasOwnProperty('value')) {
            return;
        }
        listenOwnProperty(context, this._toInputElement, 'value', listener);
    };
    DateRangePickerAdapter.prototype.setFromInputValueChangedListener = function (context, listener) {
        if (this._fromInputElement.hasOwnProperty('value')) {
            return;
        }
        listenOwnProperty(context, this._fromInputElement, 'value', listener);
    };
    DateRangePickerAdapter.prototype.setActiveDate = function (date) {
        if (this._calendarDropdown) {
            this._calendarDropdown.setActiveDate(date);
        }
    };
    return DateRangePickerAdapter;
}(BaseAdapter));
export { DateRangePickerAdapter };
